import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { environment } from '../../../../environments/environment';
import { SharedService } from '../../services/shared.service';
import { ButtonComponent } from '../button/button.component';
import { TextFieldComponent } from '../controls/text-field/text-field.component';
import { SkeletonLoaderComponent } from '../skeleton-loader/skeleton-loader.component';
import { customValidator } from '../../../Utils/validator';

type AuthnResponse = {
  flowId?: string;
  flowStatus?: string;
  authData?: {
    code?: string;
    state?: string;
    session_state?: string;
  };
  nextStep?: {
    messages?: Array<{
      context?: Array<{ key?: string; value?: string }>;
    }>;
  };
};

@Component({
  standalone: true,
  selector: 'app-mfa-email-otp',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    ButtonComponent,
    TextFieldComponent,
    SkeletonLoaderComponent
  ],
  templateUrl: './mfa-email-otp.component.html',
  styleUrl: './mfa-email-otp.component.scss'
})
export class MfaEmailOtpComponent implements OnInit, OnDestroy {
  @Input() flowId = '';
  @Input() maskedEmail = '';
  @Input() authenticatorId = '';

  @Output() verified = new EventEmitter<AuthnResponse>();
  @Output() closed = new EventEmitter<void>();

  otpForm: FormGroup;
  otpControl!: FormControl<string>;
  loading = false;
  errorMessage = '';
  readonly countdownSecondsTotal = 120;
  countdownSecondsRemaining = 120;
  private countdownTimer: ReturnType<typeof setInterval> | null = null;
  private readonly authnUrl = environment.wso2AuthnUrl;

  constructor(
    private fb: FormBuilder,
    private http: HttpClient,
    private sharedService: SharedService
  ) {
    this.otpForm = this.fb.group({
      otp: ['', [Validators.required, customValidator('numberOnly'), customValidator('safeText')]]
    });
    this.otpControl = this.otpForm.get('otp') as FormControl<string>;
  }

  ngOnInit(): void {
    this.startCountdown();
  }

  ngOnDestroy(): void {
    this.clearCountdown();
  }

  get formattedCountdown(): string {
    const minutes = Math.floor(this.countdownSecondsRemaining / 60);
    const seconds = this.countdownSecondsRemaining % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }

  get canResendOtp(): boolean {
    return this.countdownSecondsRemaining === 0 && !this.loading;
  }

  closeModal(): void {
    if (this.loading) {
      return;
    }
    this.closed.emit();
  }

  resendOtp(): void {
    if (!this.canResendOtp || !this.flowId || !this.authenticatorId) {
      return;
    }

    this.errorMessage = '';
    this.loading = true;
    this.http.post<AuthnResponse>(this.authnUrl, {
      flowId: this.flowId,
      selectedAuthenticator: {
        authenticatorId: this.authenticatorId,
        params: {
          resendCode: 'true'
        }
      }
    }).subscribe({
      next: (response) => {
        this.loading = false;
        this.flowId = response?.flowId || this.flowId;
        this.maskedEmail = this.extractMaskedEmail(response) || this.maskedEmail;
        this.sharedService.toastMessage('SUCCESS', 'EMAIL_OTP_RESENT', 'success');
        this.startCountdown();
      },
      error: () => {
        this.loading = false;
        this.errorMessage = 'TOAST_MESSAGES.TECHNICAL_ERROR';
      }
    });
  }

  verifyOtp(): void {
    if (this.loading || !this.flowId || !this.authenticatorId) {
      return;
    }

    this.errorMessage = '';
    this.otpControl.markAsTouched();
    if (this.otpControl.invalid) {
      return;
    }

    this.loading = true;
    this.http.post<AuthnResponse>(this.authnUrl, {
      flowId: this.flowId,
      selectedAuthenticator: {
        authenticatorId: this.authenticatorId,
        params: {
          OTPCode: (this.otpControl.value || '').toString().trim()
        }
      }
    }).subscribe({
      next: (response) => {
        this.loading = false;
        if (response?.flowStatus === 'SUCCESS_COMPLETED') {
          this.sharedService.toastMessage('SUCCESS', 'EMAIL_OTP_VERIFIED', 'success');
          this.verified.emit(response);
          return;
        }
        this.errorMessage = 'VERIFY.INVALID_OTP';
      },
      error: () => {
        this.loading = false;
        this.errorMessage = 'TOAST_MESSAGES.TECHNICAL_ERROR';
      }
    });
  }

  private startCountdown(seconds: number = this.countdownSecondsTotal): void {
    this.clearCountdown();
    this.countdownSecondsRemaining = seconds;
    this.countdownTimer = setInterval(() => {
      if (this.countdownSecondsRemaining <= 0) {
        this.clearCountdown();
        return;
      }
      this.countdownSecondsRemaining -= 1;
    }, 1000);
  }

  private clearCountdown(): void {
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer);
      this.countdownTimer = null;
    }
  }

  private extractMaskedEmail(response: AuthnResponse): string {
    const messages = response?.nextStep?.messages || [];
    for (const message of messages) {
      const context = Array.isArray(message?.context) ? message.context : [];
      const emailContext = context.find((item) => item?.key === 'maskedEmail');
      if (emailContext?.value) {
        return String(emailContext.value);
      }
    }
    return '';
  }
}
